import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  CheckCircle2,
  AlertCircle,
  CircleDashed,
  ShieldAlert,
  Users2,
  FileText,
  TrendingUp,
  History,
  Search,
} from 'lucide-react';
import Breadcrumb from '../../components/Breadcrumb';
import { api } from '../../lib/api';
import type { ReadershipOverview, AckLogEntry } from '@shared/types';

type Tab = 'summary' | 'chapters' | 'users' | 'acks';

export default function AdminReadership() {
  const [tab, setTab] = useState<Tab>('summary');
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'readership', 'overview'],
    queryFn: () => api.get<ReadershipOverview>('/admin/readership/overview'),
  });

  return (
    <div>
      <Breadcrumb items={[{ label: 'Admin' }, { label: 'Čtenost' }]} />
      <h1 className="mb-8 font-heading text-3xl font-bold text-dark">Čtenost</h1>

      <div className="mb-6 inline-flex rounded-md border border-gray-200 bg-white p-0.5 text-xs font-semibold">
        <TabBtn active={tab === 'summary'} onClick={() => setTab('summary')} icon={<TrendingUp className="h-3.5 w-3.5" />}>
          Souhrn
        </TabBtn>
        <TabBtn active={tab === 'chapters'} onClick={() => setTab('chapters')} icon={<FileText className="h-3.5 w-3.5" />}>
          Per kapitola
        </TabBtn>
        <TabBtn active={tab === 'users'} onClick={() => setTab('users')} icon={<Users2 className="h-3.5 w-3.5" />}>
          Per uživatel
        </TabBtn>
        <TabBtn active={tab === 'acks'} onClick={() => setTab('acks')} icon={<History className="h-3.5 w-3.5" />}>
          Potvrzené acky
        </TabBtn>
      </div>

      {tab === 'acks' ? (
        <AcksView />
      ) : isLoading || !data ? (
        <div className="text-sm text-gray-500">Načítám…</div>
      ) : tab === 'summary' ? (
        <SummaryView data={data} />
      ) : tab === 'chapters' ? (
        <ChaptersView data={data} />
      ) : (
        <UsersView data={data} />
      )}
    </div>
  );
}

function AcksView() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'acknowledgements'],
    queryFn: () =>
      api.get<{ acknowledgements: AckLogEntry[] }>('/admin/acknowledgements'),
  });
  const [filter, setFilter] = useState('');

  const entries = data?.acknowledgements ?? [];
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.userName.toLowerCase().includes(q) ||
        e.userEmail.toLowerCase().includes(q) ||
        e.chapterTitleCs.toLowerCase().includes(q),
    );
  }, [entries, filter]);

  if (isLoading) return <div className="text-sm text-gray-500">Načítám…</div>;

  return (
    <div className="grid gap-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtr (jméno, e-mail nebo kapitola)…"
            className="input pl-8"
          />
        </div>
        <div className="text-xs text-gray-500">
          {filtered.length} / {entries.length} záznamů
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
              <th className="px-4 py-3 text-left">Uživatel</th>
              <th className="px-4 py-3 text-left">Kapitola</th>
              <th className="px-4 py-3 text-center">Verze</th>
              <th className="px-4 py-3 text-left">Datum a čas</th>
              <th className="px-4 py-3 text-center">Stav</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                  {entries.length === 0
                    ? 'Zatím žádné odsouhlasené potvrzení.'
                    : 'Filtr neodpovídá žádný záznam.'}
                </td>
              </tr>
            ) : (
              filtered.map((e) => {
                const date = new Date(e.acknowledgedAt);
                const datestr = date.toLocaleString('cs-CZ', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });
                return (
                  <tr
                    key={`${e.userId}:${e.chapterId}:${e.versionId}`}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-dark">
                        {e.userName}
                        {!e.userIsActive && (
                          <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-500">
                            neaktivní
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{e.userEmail}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/c/${e.sectionSlug}/${e.chapterId}`}
                        className="font-semibold text-dark hover:text-teal"
                      >
                        {e.chapterTitleCs}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-xs text-gray-500">
                      {e.versionNumber !== null ? `v${e.versionNumber}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">{datestr}</td>
                    <td className="px-4 py-3 text-center">
                      {e.isCurrent ? (
                        <span
                          title={
                            e.snapshotMissing
                              ? 'Snapshot pro tu verzi historicky chybí, ale uživatel ack provedl. Počítá se jako platný.'
                              : 'Ack pro aktuálně publikovaný obsah.'
                          }
                          className="inline-flex items-center gap-1 rounded-md bg-positive-bg px-2 py-1 text-[11px] font-semibold text-positive"
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Aktuální
                          {e.snapshotMissing && <span className="ml-0.5 text-gray-400">*</span>}
                        </span>
                      ) : (
                        <span
                          title="Obsah kapitoly byl od tohoto ack prokazatelně změněn — vyžaduje re-ack"
                          className="inline-flex items-center gap-1 rounded-md bg-gold-soft px-2 py-1 text-[11px] font-semibold text-dark"
                        >
                          <ShieldAlert className="h-3 w-3 text-gold" />
                          Starší obsah
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryView({ data }: { data: ReadershipOverview }) {
  const { summary, byChapter, byUser } = data;
  const lowestReaders = [...byUser]
    .filter((u) => u.totalWithAccess > 0)
    .sort((a, b) => a.currentRead / a.totalWithAccess - b.currentRead / b.totalWithAccess)
    .slice(0, 5);
  const leastReadChapters = [...byChapter]
    .filter((ch) => ch.totalWithAccess > 0)
    .sort((a, b) => a.currentReaders / a.totalWithAccess - b.currentReaders / b.totalWithAccess)
    .slice(0, 5);

  return (
    <div className="grid gap-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard
          label="Uživatelé"
          value={summary.totalUsers}
          sub="aktivní"
          color="teal"
          icon={<Users2 className="h-4 w-4" />}
        />
        <StatCard
          label="Kapitoly"
          value={summary.totalChapters}
          sub={`${summary.chaptersRequiringAck} vyžaduje ack`}
          color="gray"
          icon={<FileText className="h-4 w-4" />}
        />
        <StatCard
          label="Aktuální verze přečtena"
          value={`${Math.round(summary.currentReadRatio * 100)}%`}
          sub="napříč všemi user×kapitola páry"
          color="positive"
          icon={<CheckCircle2 className="h-4 w-4" />}
        />
        <StatCard
          label="Compliance ack"
          value={`${Math.round(summary.acknowledgedRatio * 100)}%`}
          sub="potvrzených ack-required kapitol"
          color={summary.acknowledgedRatio >= 0.9 ? 'positive' : summary.acknowledgedRatio >= 0.5 ? 'warning' : 'danger'}
          icon={<ShieldAlert className="h-4 w-4" />}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="card p-5">
          <h3 className="mb-4 font-heading text-lg font-bold text-dark">Uživatelé s nejnižší čteností</h3>
          {lowestReaders.length === 0 ? (
            <div className="text-sm text-gray-500">Žádná data.</div>
          ) : (
            <ul className="space-y-3">
              {lowestReaders.map((u) => (
                <li key={u.userId} className="flex items-center justify-between text-sm">
                  <div>
                    <div className="font-semibold text-dark">{u.name}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-semibold text-dark">
                      {u.currentRead}/{u.totalWithAccess}
                    </div>
                    {u.ackPending > 0 && (
                      <div className="text-[11px] text-gold">+{u.ackPending} ack pending</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card p-5">
          <h3 className="mb-4 font-heading text-lg font-bold text-dark">Nejméně čtené kapitoly</h3>
          {leastReadChapters.length === 0 ? (
            <div className="text-sm text-gray-500">Žádná data.</div>
          ) : (
            <ul className="space-y-3">
              {leastReadChapters.map((ch) => (
                <li key={ch.chapterId} className="flex items-center justify-between text-sm">
                  <div className="truncate pr-3">
                    <Link
                      to={`/c/${ch.sectionSlug}/${ch.chapterId}`}
                      className="font-semibold text-dark hover:text-teal"
                    >
                      {ch.titleCs}
                    </Link>
                    {ch.requiresAck && (
                      <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-gold-soft px-1.5 py-0.5 text-[10px] font-semibold text-dark">
                        <ShieldAlert className="h-2.5 w-2.5 text-gold" />
                        ack
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-sm font-semibold text-dark">
                    {ch.currentReaders}/{ch.totalWithAccess}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ChaptersView({ data }: { data: ReadershipOverview }) {
  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
            <th className="px-4 py-3 text-left">Kapitola</th>
            <th className="px-4 py-3 text-center">Přečteno aktuální</th>
            <th className="px-4 py-3 text-center">Starší verze</th>
            <th className="px-4 py-3 text-center">Nečteno</th>
            <th className="px-4 py-3 text-center">Ack potvrzeno</th>
          </tr>
        </thead>
        <tbody>
          {data.byChapter.map((ch) => {
            const pct = ch.totalWithAccess > 0 ? (ch.currentReaders / ch.totalWithAccess) * 100 : 0;
            return (
              <tr key={ch.chapterId} className="border-b border-gray-100 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-semibold text-dark">{ch.titleCs}</div>
                  {ch.requiresAck && (
                    <span className="mt-0.5 inline-flex items-center gap-0.5 rounded-full bg-gold-soft px-1.5 py-0.5 text-[10px] font-semibold text-dark">
                      <ShieldAlert className="h-2.5 w-2.5 text-gold" />
                      Ack
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-1.5 text-positive">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span className="font-mono font-semibold">{ch.currentReaders}</span>
                  </div>
                  <div className="mt-1 h-1 w-24 overflow-hidden rounded-full bg-gray-100 mx-auto">
                    <div className="h-full bg-positive" style={{ width: `${pct}%` }} />
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center gap-1.5 text-gray-500">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span className="font-mono">{ch.staleReaders}</span>
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center gap-1.5 text-gray-400">
                    <CircleDashed className="h-3.5 w-3.5" />
                    <span className="font-mono">{ch.neverReaders}</span>
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  {ch.requiresAck ? (
                    <span
                      className={clsx(
                        'font-mono font-semibold',
                        ch.acknowledgedCount === ch.currentReaders && ch.currentReaders > 0
                          ? 'text-positive'
                          : 'text-gold',
                      )}
                    >
                      {ch.acknowledgedCount}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UsersView({ data }: { data: ReadershipOverview }) {
  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
            <th className="px-4 py-3 text-left">Uživatel</th>
            <th className="px-4 py-3 text-center">Dostupné</th>
            <th className="px-4 py-3 text-center">Aktuální</th>
            <th className="px-4 py-3 text-center">Starší</th>
            <th className="px-4 py-3 text-center">Nečteno</th>
            <th className="px-4 py-3 text-center">Ack pending</th>
          </tr>
        </thead>
        <tbody>
          {data.byUser.map((u) => {
            const pct = u.totalWithAccess > 0 ? (u.currentRead / u.totalWithAccess) * 100 : 0;
            return (
              <tr key={u.userId} className="border-b border-gray-100 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-semibold text-dark">{u.name}</div>
                  <div className="text-xs text-gray-500">
                    {u.email} · {u.role}
                  </div>
                </td>
                <td className="px-4 py-3 text-center font-mono text-gray-500">{u.totalWithAccess}</td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-1.5 text-positive">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span className="font-mono font-semibold">{u.currentRead}</span>
                  </div>
                  <div className="mt-1 h-1 w-24 overflow-hidden rounded-full bg-gray-100 mx-auto">
                    <div className="h-full bg-positive" style={{ width: `${pct}%` }} />
                  </div>
                </td>
                <td className="px-4 py-3 text-center font-mono text-gray-500">{u.staleRead}</td>
                <td className="px-4 py-3 text-center font-mono text-gray-400">{u.neverRead}</td>
                <td className="px-4 py-3 text-center">
                  {u.ackPending > 0 ? (
                    <span className="inline-flex items-center gap-1 font-mono font-semibold text-gold">
                      <ShieldAlert className="h-3.5 w-3.5" />
                      {u.ackPending}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
  icon,
}: {
  label: string;
  value: string | number;
  sub?: string;
  color: 'teal' | 'gold' | 'positive' | 'warning' | 'danger' | 'gray';
  icon: React.ReactNode;
}) {
  const bg = {
    teal: 'bg-teal-light text-teal',
    gold: 'bg-gold-soft text-gold',
    positive: 'bg-positive-bg text-positive',
    warning: 'bg-warning-bg text-warning',
    danger: 'bg-danger/10 text-danger',
    gray: 'bg-gray-100 text-gray-500',
  }[color];

  return (
    <div className="card p-5">
      <div className={clsx('mb-3 inline-flex h-8 w-8 items-center justify-center rounded-md', bg)}>{icon}</div>
      <div className="font-heading text-3xl font-bold text-dark">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wide text-gray-500">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 rounded px-3 py-1.5 transition-colors',
        active ? 'bg-dark text-white' : 'text-gray-500 hover:text-dark',
      )}
    >
      {icon}
      {children}
    </button>
  );
}
